/**
 * Carries a post-sign-in destination across an SSO round trip.
 *
 * The password form keeps the `redirect` search param in the page and navigates
 * to it itself. The SSO buttons cannot: they hand the browser to the Control
 * Plane (`/api/v1/auth/google|microsoft`), which bounces through the identity
 * provider and comes back to `/auth/callback` with a token payload it built
 * itself — and CP does not echo a `redirect` it was never given.
 *
 * Without this, a user who arrives at `/oauth/consent?request_id=…`
 * unauthenticated, is sent to sign in, and chooses Microsoft or Google, lands on
 * the dashboard instead. The authorisation they were in the middle of is parked
 * at CP with a 10-minute life and simply expires; their Claude reports a
 * timeout with nothing to explain it.
 *
 * `sessionStorage`, not `localStorage`: the value is one leg of one journey in
 * one tab. It is read exactly once and cleared, so a stale destination can
 * never hijack a later, unrelated sign-in.
 *
 * Every value in and out passes `isSafePortalRedirect` — this is a
 * navigation target that originates in a URL, so it carries the same
 * open-redirect risk as the `redirect` query param and gets the same guard.
 */

import { isSafePortalRedirect } from "./auth-redirect";

const STORAGE_KEY = "lara:post-sso-redirect";

/** Remember where to land after an SSO round trip. No-op for an unsafe value. */
export function rememberPostSsoRedirect(target: unknown): void {
  try {
    if (!isSafePortalRedirect(target)) {
      // An unusable target must not leave a PREVIOUS one in place, or the next
      // SSO sign-in silently inherits a destination from an earlier attempt.
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, target);
  } catch {
    // Storage can be unavailable (private mode, blocked site data). Losing the
    // destination degrades to landing on the dashboard — the behaviour before
    // this existed — which is why this is swallowed rather than surfaced.
  }
}

/**
 * Read the remembered destination WITHOUT clearing it.
 *
 * For callers that may run more than once for a single navigation — a
 * TanStack `beforeLoad` does, and a destructive read there consumed the value
 * on the first pass and threw its redirect with nothing on the second (caught
 * by tests/playwright/platform/cp-frontend-targets.spec.ts before it shipped).
 *
 * Leaving the value in place is safe: it can only be consumed by
 * `/auth/callback`, which is reachable only through an SSO round trip. The
 * sign-IN buttons call `rememberPostSsoRedirect` first, which overwrites the
 * value or clears it. The sign-UP form does not (it posts its own form data to
 * `/auth/{provider}/signup`), so a user who abandons an SSO sign-in
 * mid-consent and then signs UP with SSO in the same tab lands on the stashed
 * destination — which is allow-listed, and is where they were going.
 */
export function peekPostSsoRedirect(): string | undefined {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return isSafePortalRedirect(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/** Read and CLEAR the remembered destination. Returns undefined when unusable. */
export function takePostSsoRedirect(): string | undefined {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return isSafePortalRedirect(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Where `/auth/callback` sends the user once a session exists.
 *
 * Order: an echoed `redirect` on the callback URL, then the destination
 * stashed before the SSO round trip, then the dashboard. The URL value is
 * validated rather than trusted — this page is directly reachable and its
 * params feed straight into a navigate, so an unvalidated value is an
 * open-redirect primitive.
 *
 * By default reading the stash CONSUMES it, including when an echoed value
 * wins, so a spent destination can never be replayed into a later sign-in.
 * Pass `{consume: false}` where the call must be repeatable — a React
 * `useState` initializer is double-invoked under StrictMode, so a destructive
 * read there answers differently the second time.
 */
export function resolvePostAuthRedirect(
  searchRedirect: unknown,
  options: { consume?: boolean } = {},
): string {
  const { consume = true } = options;
  const stashed = consume ? takePostSsoRedirect() : peekPostSsoRedirect();
  if (isSafePortalRedirect(searchRedirect)) return searchRedirect;
  return stashed ?? "/portal";
}
